/** Phase 4.3-β — Approval chain instances list.
 *
 * Lists multi-step chain instances scoped to the current tenant. Single-row
 * legacy approvals stay on /app/approvals (Approvals.jsx) — this page is the
 * chain-only surface. See ADR-0028 / ADR-0029.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const STATUS_VARIANTS = {
  pending: "secondary",
  approved: "default",
  rejected: "destructive",
  cancelled: "outline",
  expired: "outline",
};

function StatusBadge({ status }) {
  return (
    <Badge
      data-testid={`chain-status-${status}`}
      variant={STATUS_VARIANTS[status] || "outline"}
    >
      {status}
    </Badge>
  );
}

export default function ApprovalChains() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [defs, setDefs] = useState([]);
  const [status, setStatus] = useState(params.get("status") || "pending");
  const [defKey, setDefKey] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const p = { limit: 50 };
    if (status !== "all") p.status = status;
    if (defKey !== "all") {
      const def = defs.find((d) => d.key === defKey);
      if (def) p.chain_definition_id = def.id;
    }
    try {
      const r = await api.get("/approval-chains/instances", { params: p });
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/approval-chains/definitions")
      .then((r) => setDefs(r.data?.items || []))
      .catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, defKey]);

  const defByKey = useMemo(() => {
    const m = {};
    for (const d of defs) m[d.id] = d;
    return m;
  }, [defs]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const lc = q.toLowerCase();
    return items.filter((x) =>
      (x.subject?.id || "").toLowerCase().includes(lc) ||
      (x.subject?.kind || "").toLowerCase().includes(lc) ||
      (defByKey[x.chain_definition_id]?.key || "").toLowerCase().includes(lc)
    );
  }, [items, q, defByKey]);

  return (
    <div data-testid="approval-chains-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("chains.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("chains.subtitle")}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              data-testid="chains-search"
              placeholder={t("chains.search_placeholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Select
              value={status}
              onValueChange={(v) => { setStatus(v); setParams({ status: v }); }}
            >
              <SelectTrigger data-testid="chains-status-filter">
                <SelectValue placeholder={t("chains.status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("chains.status_all")}</SelectItem>
                <SelectItem value="pending">{t("chains.status_pending")}</SelectItem>
                <SelectItem value="approved">{t("chains.status_approved")}</SelectItem>
                <SelectItem value="rejected">{t("chains.status_rejected")}</SelectItem>
                <SelectItem value="cancelled">{t("chains.status_cancelled")}</SelectItem>
                <SelectItem value="expired">{t("chains.status_expired")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={defKey} onValueChange={setDefKey}>
              <SelectTrigger data-testid="chains-definition-filter">
                <SelectValue placeholder={t("chains.definition")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("chains.definition_all")}</SelectItem>
                {defs.map((d) => (
                  <SelectItem key={d.id} value={d.key}>{d.key}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={load}
              data-testid="chains-refresh"
            >
              {t("chains.refresh")}
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("chains.col_created")}</TableHead>
                <TableHead>{t("chains.col_definition")}</TableHead>
                <TableHead>{t("chains.col_subject")}</TableHead>
                <TableHead>{t("chains.col_status")}</TableHead>
                <TableHead>{t("chains.col_step")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-sm text-muted-foreground py-6"
                  >
                    {t("chains.loading")}
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-sm text-muted-foreground py-8"
                    data-testid="chains-empty"
                  >
                    {t("chains.empty")}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((x) => {
                const def = defByKey[x.chain_definition_id];
                const total = (def?.steps || []).length || x.step_runs?.length || 0;
                const idx = (x.current_step_index ?? 0) + 1;
                const currentName = (
                  def?.steps?.[x.current_step_index]?.name ||
                  def?.steps?.[x.current_step_index]?.step_id ||
                  x.current_step_id || "—"
                );
                return (
                  <TableRow
                    key={x.id}
                    data-testid={`chain-row-${x.id}`}
                    className="cursor-pointer hover:bg-accent/30"
                  >
                    <TableCell className="text-xs">
                      {x.created_at
                        ? new Date(x.created_at).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {def?.key || x.chain_definition_id?.slice(0, 8)}
                      {def?.version > 1 && (
                        <span className="ml-1 text-muted-foreground">
                          v{def.version}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono">{x.subject?.kind}</span>
                      {" / "}
                      <span className="font-mono">
                        {(x.subject?.id || "").slice(0, 12)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={x.status} />
                    </TableCell>
                    <TableCell className="text-xs">
                      {x.status === "pending"
                        ? `${idx}/${total} · ${currentName}`
                        : `${total}/${total}`}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        asChild
                        data-testid={`chain-open-${x.id}`}
                      >
                        <Link to={`/app/approval-chains/${x.id}`}>
                          {t("chains.open")}
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
